import { ContactEntity } from '../contact/contact.options';
import { Entity } from '../entity';


export enum ServiceConnectionType {
    Xero = 'Xero',
    Myob = 'Myob',
    MYOBAcumatica = 'MYOB Acumatica',
    QuickBooks = 'QuickBooks',
    WorkBuddy = 'WorkBuddy Connect',
    Stripe = 'Stripe',
    What3Words = 'What3Words',
    SMS = 'SMS',
    OpenAI = 'OpenAI',
    PropertyMe = 'PropertyMe',
    PropertyTree = 'PropertyTree',
    PropertyMeCustomer = 'PropertyMeCustomer',
    PropertyTreeCustomer = 'PropertyTreeCustomer',
    AddressValidation = "Address Validation",
    // SchedulingAutomation = 'SchedulingAutomation',
    Jobpac = 'Viewpoint Jobpac Connect',
    Anthropic = 'Anthropic',
    HomesNSW = 'HomesNSW',
    BGISHomesVIC = 'BGISHomesVIC',
    BGISManhattanONE = 'BGISManhattanONE',
    WBImporter = 'Workbuddy Importer',
}


export interface ServiceConnectionSummary {
    _id: string;
    name: string;
    type: ServiceConnectionType;
    authenticated: boolean;
    email: string;
    expires: Date;
    tenants: Entity[];
    entity?: Entity | ContactEntity;
    settings?: { [key: string]: any }
    data?: any;
    secrets?: { [path: string]: boolean };
}

export interface PropertyMeConnectionSettings {
    tenantTags?: Entity[],
    propertyManagerTags?: Entity[]
}

export interface AcumaticaConnectionSettings {
    instanceUrl: string;
    clientId: string;
    clientSecret: string;
    redirectUrl?: string;
    username: string;
    password: string;
    company: string;
    branch?: string;
}

export interface ServiceConnection extends ServiceConnectionSummary {
    tokens: any;
}

export interface ServiceConnectionStartAuthenticationResponse {
    success: boolean;
    authUrl: string;
    item: ServiceConnectionSummary;
    error?: any;
}


export interface ServiceConnectionTokenInfo {
    serviceConnectionId: string;
    tenants: Entity[];
    tokens: any;
    email: string;
    expiry: Date;
}

export interface ServiceConnectionInfo {
    _id: string;
    tenant: Entity;
}

export interface ServiceConnectionKeySecretToken {
    provider: string;
    key: string;
    secret: string;
    endpoint: string;
    orgId: string;
    apiKey?: string;
    authMode?: 'microsoft' | 'bgis-apim';
}

// Every Manhattan call goes through the BGIS middleware in Azure: a Microsoft client-credentials
// token (like Homes VIC) is sent as the bearer on each request, and the middleware manages the
// Manhattan session itself, so no session id is stored or sent.
export interface ManhattanOneConnectionTokens {
    // BGIS middleware base URL; the Manhattan paths (SPDEDMHAPI.GRIDGET, SPDEDMHAPI.SVC) hang off it
    middlewareUrl: string;
    // Client-credentials set (Client Id / Client Secret) and the token endpoint
    key?: string;
    secret?: string;
    authorisationUrl?: string;

    // Obtained on authorise: the middleware bearer token
    access_token?: string;
}

// Mapping tables translating WorkBuddy reference data into the Manhattan codes every financial
// document must carry. All codes are typed in manually; WorkBuddy references are stored as
// { _id, name } and matched on _id. "Zone" is the WorkBuddy zone (Settings › Zones), not a zone tag.
export interface ManhattanOneZoneMapping {
    zone: Entity;
    // Sent as PROPERTY on document lines
    propertyCode: string;
}

export interface ManhattanOneJobTypeMapping {
    jobType: Entity;
    // Sent as CHARGE_CODE on document lines
    chargeCode: string;
}

export interface ManhattanOneCustomerMapping {
    customer: Entity;
    // Sent as TENANT_REFERENCE on client invoice headers
    customerReference: string;
}

export interface ManhattanOneLeaseCodeMapping {
    customer: Entity;
    zone: Entity;
    // Sent as LEASE on client invoice lines
    leaseCode: string;
}

// A Manhattan supplier as offered in the contractor mapping picker
export interface ManhattanOneSupplierOption {
    // SUP_REF — what the WorkBuddy contractor stores as its integration id
    reference: string;
    name: string;
    onHold?: boolean;
}

export type ManhattanOneContractorSyncStatus = 'Running' | 'Completed' | 'Failed';

// Who started a run: someone pressing "Sync now", or the schedule on the connection
export type ManhattanOneContractorSyncTrigger = 'Manual' | 'Scheduled';

// "Sync now" pressed, stored on the connection as data.contractorSyncRequest. The API only records it;
// the tenant connect worker starts the run on its next check and clears it in the same write.
export interface ManhattanOneContractorSyncRequest {
    requestedAt: Date;
    dryRun: boolean;
}

// How often the contractor sync runs by itself, counted from the start of the last live run; off unless enabled
export interface ManhattanOneContractorSyncSchedule {
    enabled: boolean;
    intervalMinutes: number;
}

export interface ManhattanOneContractorSyncError {
    // Manhattan supplier reference (SUP_REF)
    reference: string;
    name?: string;
    message: string;
}

// Outcome of the last contractor sync, stored on the connection as data.contractorSync so the
// connection screen can show it. Manhattan is the source of truth for contractors, but WorkBuddy
// never deactivates them — contractors that dropped out of Manhattan's list or are on hold are tagged.
export interface ManhattanOneContractorSyncSummary {
    status: ManhattanOneContractorSyncStatus;
    // Preview: everything is calculated but nothing is written
    dryRun: boolean;
    // Absent on runs recorded before scheduling existed, which were all manual
    trigger?: ManhattanOneContractorSyncTrigger;
    startedAt: Date;
    // Last time the run reported progress; a Running summary that stops updating means the worker process died mid-run
    updatedAt?: Date;
    finishedAt?: Date;
    // Supplier rows read from Manhattan
    read: number;
    created: number;
    updated: number;
    unchanged: number;
    taggedOnHold: number;
    taggedNotInList: number;
    failed: number;
    errors: ManhattanOneContractorSyncError[];
    message?: string;
}

// What the contractor sync keeps on the connection's data
export interface ManhattanOneConnectionData {
    contractorSync?: ManhattanOneContractorSyncSummary;
    contractorSyncRequest?: ManhattanOneContractorSyncRequest | null;
    // Start of the last live attempt, manual or scheduled; the schedule counts its interval from here. Kept
    // apart from the summary because a preview overwrites the summary and a refused attempt writes none.
    contractorSyncLastAttemptAt?: Date;
}

// The account labels the contractor sync applies instead of deactivating contractors; chosen on the connection
export interface ManhattanOneContractorTags {
    // Put on a contractor whose Manhattan supplier is on hold, removed when it comes off hold
    onHold?: Entity;
    // Put on a contractor whose reference is no longer in Manhattan's supplier list, removed when it reappears
    notInList?: Entity;
}

export interface ManhattanOneConnectionSettings {
    zoneMappings: ManhattanOneZoneMapping[];
    jobTypeMappings: ManhattanOneJobTypeMapping[];
    customerMappings: ManhattanOneCustomerMapping[];
    leaseCodeMappings: ManhattanOneLeaseCodeMapping[];
    contractorTags?: ManhattanOneContractorTags;
    // Manhattan's C_REF for the client account (program) this tenant serves, e.g. LAHCNSWOAU. The
    // contractor sync and the supplier picker read only that account's suppliers and refuse to run
    // while it is blank — an unfiltered read is every supplier BGIS has, across every program.
    supplierClientReference?: string;
    contractorSync?: ManhattanOneContractorSyncSchedule;
}